import type { ComposerTranslation } from "vue-i18n"
import type { I18nString } from "@/lang/config"
import type { AgeWeeksDays, MaternityLeave } from "@/types/common"
import type { CRLGestationalAge, Percentiles, PregnancyCalculatedData } from "@/types/pregnancy"
import _find from "lodash/find"
import _findLast from "lodash/findLast"
import { usePregnancyStore } from "@/stores/pregnancy"
import dayjs, { dFormat } from "@/utils/dayjs"
import { toNumber } from "./formatter"

export interface PregnancyWeekItem {
	week: number
	from: Date
	to: Date
	current: boolean
}

export interface PregnancyMonthItem {
	month: number
	from: Date
	to: Date
	current: boolean
}

export function getPregnancyWeeksList(args: {
	week?: number | null
	lmp?: Date | string | number | null
	maxWeeks: number
}): PregnancyWeekItem[] {
	const today = dayjs()
	const currentDay = today.startOf("day")
	const lmpDate = args.week != null ? today.subtract(Math.max(args.week - 1, 0), "week") : dayjs(args.lmp || today)

	return Array.from({ length: args.maxWeeks }, (_, index) => {
		const week = index + 1
		const from = lmpDate.add(week - 1, "week")
		const to = from.add(6, "day")
		const fromDay = from.startOf("day")
		const toDay = to.startOf("day")

		return {
			week,
			from: from.toDate(),
			to: to.toDate(),
			current: currentDay.valueOf() >= fromDay.valueOf() && currentDay.valueOf() <= toDay.valueOf()
		}
	})
}

export function getPregnancyMonthsList(args: {
	week?: number | null
	lmp?: Date | string | number | null
	maxMonths?: number
}): PregnancyMonthItem[] {
	const today = dayjs()
	const currentDay = today.startOf("day")
	const lmpDate = args.week != null ? today.subtract(Math.max(args.week - 1, 0), "week") : dayjs(args.lmp || today)

	return Array.from({ length: args.maxMonths || 9 }, (_, index) => {
		const month = index + 1
		const from = lmpDate.add(month - 1, "month")
		const to = lmpDate.add(month, "month").subtract(1, "day")
		const fromDay = from.startOf("day")
		const toDay = to.startOf("day")

		return {
			month,
			from: from.toDate(),
			to: to.toDate(),
			current: currentDay.valueOf() >= fromDay.valueOf() && currentDay.valueOf() <= toDay.valueOf()
		}
	})
}

export function calculateFromLastMenstrualPeriod(args: {
	lmp: Date
	reference: Date
	redatingDays?: number
}): PregnancyCalculatedData {
	const lmpDate = dayjs(args.lmp) // Data ultima mestruazione
	const refDate = dayjs(args.reference) // Data di riferimento (default: oggi)

	const pregnancyStore = usePregnancyStore()

	// Data ultima mestruazione (LMP + ridatazione)
	const lastMenstrualRedated = lmpDate.add(args.redatingDays || 0, "day").toDate()

	// Calcolo epoca gestazionale
	const gestationalDays = refDate.diff(lastMenstrualRedated, "day")
	const gestationalWeeks = Math.max(0, Math.floor(gestationalDays / 7))
	const gestationalRemainingDays = Math.max(0, gestationalDays % 7)

	// Data concepimento (Data ultima mestruazione + 14 giorni)
	const conceptionDate = dayjs(lastMenstrualRedated).add(pregnancyStore.conceptionDays, "day").toDate()

	// Calcolo epoca concezionale
	const conceptionDays = refDate.diff(conceptionDate, "day")
	const conceptionWeeks = Math.max(0, Math.floor(conceptionDays / 7))
	const conceptionRemainingDays = Math.max(0, conceptionDays % 7)

	// Data presunta parto (Data ultima mestruazione + 280 giorni)
	const estimatedDueDate = dayjs(lastMenstrualRedated).add(pregnancyStore.pregnancyDays, "day").toDate()

	return {
		gestationalAge: { weeks: gestationalWeeks, days: gestationalRemainingDays },
		conceptionalAge: { weeks: conceptionWeeks, days: conceptionRemainingDays },
		lastMenstrualPeriod: lmpDate.toDate(),
		lastMenstrualRedated,
		conceptionDate,
		estimatedDueDate,
		motherWeightGain: calculateMotherWeightGain(gestationalDays),
		fetalWeight: calculateFetalWeight(gestationalDays)
	}
}

export function calculateFromConception(args: { doc: Date; reference: Date }): PregnancyCalculatedData {
	const pregnancyStore = usePregnancyStore()

	const lmp = dayjs(args.doc).subtract(pregnancyStore.conceptionDays, "day").toDate()
	return calculateFromLastMenstrualPeriod({ lmp, reference: args.reference })
}

export function calculateFromEstimatedDueDate(args: { edd: Date; reference: Date }): PregnancyCalculatedData {
	const pregnancyStore = usePregnancyStore()

	const lmp = dayjs(args.edd).subtract(pregnancyStore.pregnancyDays, "day").toDate()
	return calculateFromLastMenstrualPeriod({ lmp, reference: args.reference })
}

function calculateMotherWeightGain(gestationalDays: number): Percentiles {
	const gestationalWeeks = Math.floor(gestationalDays / 7)
	const gestationalRemainingDays = gestationalDays % 7
	const totalWeeks = gestationalWeeks + gestationalRemainingDays / 7

	const pregnancyStore = usePregnancyStore()

	// Trova i valori più vicini nella tabella dei percentili
	const lower = _findLast(pregnancyStore.motherWeightGain, x => x.week <= totalWeeks)
	const upper = _find(pregnancyStore.motherWeightGain, x => x.week >= totalWeeks)

	if (!lower || !upper) {
		return { p10: 0, p50: 0, p90: 0 }
	}
	if (lower.week === upper.week) {
		return {
			p10: lower.p10,
			p50: lower.p50,
			p90: lower.p90
		}
	}

	// Interpolazione lineare
	const ratio = (totalWeeks - lower.week) / (upper.week - lower.week)

	const lowerP10 = lower.p10 ?? 0
	const upperP10 = upper.p10 ?? 0
	const lowerP50 = lower.p50 ?? 0
	const upperP50 = upper.p50 ?? 0
	const lowerP90 = lower.p90 ?? 0
	const upperP90 = upper.p90 ?? 0

	return {
		p10: Math.round(lowerP10 + (upperP10 - lowerP10) * ratio),
		p50: Math.round(lowerP50 + (upperP50 - lowerP50) * ratio),
		p90: Math.round(lowerP90 + (upperP90 - lowerP90) * ratio)
	}
}

function calculateFetalWeight(gestationalDays: number): Percentiles {
	const gestationalWeeks = Math.floor(gestationalDays / 7)
	const gestationalRemainingDays = gestationalDays % 7
	const totalWeeks = gestationalWeeks + gestationalRemainingDays / 7

	const pregnancyStore = usePregnancyStore()

	// Trova i valori più vicini nella tabella dei percentili
	const lower = _findLast(pregnancyStore.fetalWeightPercentiles, x => x.week <= totalWeeks)
	const upper = _find(pregnancyStore.fetalWeightPercentiles, x => x.week >= totalWeeks)

	if (!lower || !upper) {
		return { p10: 0, p50: 0, p90: 0 }
	}
	if (lower.week === upper.week) {
		return {
			p10: lower.p10,
			p50: lower.p50,
			p90: lower.p90
		}
	}

	// Interpolazione lineare
	const ratio = (totalWeeks - lower.week) / (upper.week - lower.week)

	const lowerP10 = lower.p10 ?? 0
	const upperP10 = upper.p10 ?? 0
	const lowerP50 = lower.p50 ?? 0
	const upperP50 = upper.p50 ?? 0
	const lowerP90 = lower.p90 ?? 0
	const upperP90 = upper.p90 ?? 0

	return {
		p10: Math.round(lowerP10 + (upperP10 - lowerP10) * ratio),
		p50: Math.round(lowerP50 + (upperP50 - lowerP50) * ratio),
		p90: Math.round(lowerP90 + (upperP90 - lowerP90) * ratio)
	}
}

/**
 * Trova l'età gestazionale per un dato CRL con interpolazione lineare
 */
export function estimateGestationalAgeFromCRL(mm: number): null | CRLGestationalAge {
	const pregnancyStore = usePregnancyStore()
	const gestationalAgeCRL = pregnancyStore.gestationalAgeCRL

	if (!gestationalAgeCRL?.length) return null

	const firstItem = gestationalAgeCRL[0]
	const lastItem = gestationalAgeCRL.at(-1)

	if (mm < (firstItem?.mm || 0) || mm > (lastItem?.mm || 0)) {
		return null // Fuori range
	}

	let lower = null
	let upper = null

	// Trova i due punti tra cui interpolare
	for (let i = 0; i < gestationalAgeCRL.length - 1; i++) {
		const currentItem = gestationalAgeCRL[i]
		const nextItem = gestationalAgeCRL[i + 1]

		if (!currentItem || !nextItem) continue

		if (currentItem.mm <= mm && nextItem.mm >= mm) {
			lower = currentItem
			upper = nextItem
			break
		}
	}

	if (!lower || !upper) return null

	// Interpolazione lineare
	const ratio = (mm - lower.mm) / (upper.mm - lower.mm)
	const interpolatedAgeInDays = Math.round(lower.days + ratio * (upper.days - lower.days))

	// Converti giorni in settimane + giorni
	const week = Math.floor(interpolatedAgeInDays / 7)
	const day = interpolatedAgeInDays % 7

	return {
		mm,
		days: interpolatedAgeInDays,
		weeks: { week, day }
	}
}

export function estimateCRLFromGestationalAge(days: number): number | null {
	const pregnancyStore = usePregnancyStore()
	const gestationalAgeCRL = pregnancyStore.gestationalAgeCRL

	if (!gestationalAgeCRL.length) return null

	const firstItem = gestationalAgeCRL[0]
	const lastItem = gestationalAgeCRL.at(-1)

	if (!firstItem || !lastItem || days < firstItem.days || days > lastItem.days) {
		return null
	}

	for (let i = 0; i < gestationalAgeCRL.length - 1; i++) {
		const currentItem = gestationalAgeCRL[i]
		const nextItem = gestationalAgeCRL[i + 1]

		if (!currentItem || !nextItem) continue

		if (currentItem.days === days) return currentItem.mm
		if (nextItem.days === days) return nextItem.mm

		if (currentItem.days <= days && nextItem.days >= days) {
			const ratio = (days - currentItem.days) / (nextItem.days - currentItem.days)
			return Math.round(currentItem.mm + ratio * (nextItem.mm - currentItem.mm))
		}
	}

	return null
}

/**
 * Calcola il trimestre corrispondente dato un numero di giorni.
 * Ritorna un numero da 1 a 4.
 */
export function getQuarterFromDays(days: number): number {
	const daysPerTrimester = 365 / 4 // ≈ 91.25
	const trimester = Math.ceil(days / daysPerTrimester)
	return Math.min(Math.max(trimester, 1), 4)
}

/**
 * Calcola il mese corrispondente dato un numero di giorni.
 * Ritorna un numero da 1 a 12.
 */
export function getMonthFromDays(days: number): number {
	const daysPerMonth = 365 / 12 // ≈ 30.42
	const month = Math.ceil(days / daysPerMonth)
	return Math.min(Math.max(month, 1), 12)
}

export function getGestationalQuarter(gestational_period: AgeWeeksDays) {
	return getQuarterFromDays((gestational_period?.weeks || 0) * 7 + (gestational_period?.days || 0))
}

export function getGestationalMonth(gestational_period: AgeWeeksDays) {
	return getMonthFromDays((gestational_period?.weeks || 0) * 7 + (gestational_period?.days || 0))
}

export function getGestationalLabel(
	gestational_period: AgeWeeksDays,
	options: { t: ComposerTranslation; useHtml?: boolean }
) {
	const t = options.t
	const useHtml = options.useHtml ?? false

	if (useHtml) {
		return `<strong>${gestational_period?.weeks || 0}+${gestational_period?.days || 0}</strong> (${getGestationalQuarter(gestational_period)}° ${t<I18nString>(`common.misc.quarter`)}, ${getGestationalMonth(gestational_period)}° ${t<I18nString>(`common.misc.month`)})`
	}

	return `${gestational_period?.weeks || 0}+${gestational_period?.days || 0} (${getGestationalQuarter(gestational_period)}° ${t<I18nString>(`common.misc.quarter`)}, ${getGestationalMonth(gestational_period)}° ${t<I18nString>(`common.misc.month`)})`
}

export function calculateMaternityLeaveFromEdd(estimatedDueDate: Date, type: string): MaternityLeave {
	const [monthsBefore = "0", monthsAfter = "0"] = type.split("+")

	return {
		type,
		start: dayjs(estimatedDueDate)
			.subtract(toNumber(monthsBefore) || 0, "month")
			.toDate(),
		end: dayjs(estimatedDueDate)
			.add(toNumber(monthsAfter) || 0, "month")
			.toDate()
	}
}

export function getEstimatedDueDateLabel(delivery_date: Date, options?: { days_from_delivery?: number }) {
	const daysFromDelivery =
		options?.days_from_delivery != null ? options.days_from_delivery : dayjs().diff(delivery_date, "day")

	return `${dFormat(delivery_date, "date")} ${daysFromDelivery !== 0 ? `(${dayjs().add(daysFromDelivery, "day").fromNow()})` : ""}`
}

export function isGestationalAgeBiometryRange(weeks: number) {
	const range = usePregnancyStore().gestationalAgeBiometryRangeWeeks

	if (!range) return false

	return weeks >= range.minWeek && weeks <= range.maxWeek
}

export function isGestationalAgeCRLRange(days: number) {
	const pregnancyStore = usePregnancyStore()
	const gestationalAgeCRL = pregnancyStore.gestationalAgeCRL

	if (!gestationalAgeCRL.length) return false

	const firstItem = gestationalAgeCRL[0]
	const lastItem = gestationalAgeCRL.at(-1)

	if (!firstItem || !lastItem) return false

	return days >= firstItem.days && days <= lastItem.days
}
